import { DOCUMENT, NgIf, NgStyle, NgClass } from '@angular/common';
import {Component, Inject, OnInit, ViewChild, DestroyRef, inject} from '@angular/core';
import { NgForm, NgModel, FormsModule } from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ChangePasswordForm} from '../../../models/ChangePasswordForm';
import {PasswordPolicy} from '../../models/reset-password/PasswordPolicy';
import {LoginService} from '../../../core/services/login.service';
import {UtilsService} from '../../../core/services/utils';
import {SessionStorageService} from '../../../core/services/session-storage.service';
import {PasswordPolicyNote} from '../../models/reset-password/PasswordPolicyNote';
import {PasswordPolicyResponse} from '../../models/reset-password/PasswordPolicyResponse';
import {CookieService} from 'ngx-cookie-service';
import {environment} from '../../../../environments/environment';
import {DomainDataService} from '../../../core/services/domain-data.service';
import {OAuthConstants} from '../../constant/oAuth.constants';
import {encrypt} from '../../../utils/encrypt-util';
import { EqualToDirective } from '../../directives/equal-to.directive';
import { GtLoadingComponent } from '../../../core/components/gt-loading/gt-loading.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
    selector: 'change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    imports: [NgIf, GtLoadingComponent, FormsModule, NgStyle, NgClass, EqualToDirective]
})
export class ChangePasswordComponent implements OnInit {
  public changePwdForm = <ChangePasswordForm>{};
  public passwordPolicy = <PasswordPolicy>{};
  public passwordPolicyNote;
  public domainName: string;
  public isSubmitted = false;
  public errorMessage: string;
  public isLoading = true;
  public emoji = '&#128522;';
  @ViewChild('changePassword')
  private formGroup: NgForm;
  @ViewChild('newPassword')
  public newPasswordCtrl: NgModel;
  public checkListCount = 0;
  public currentCheckListCount = 0;
  private checkListPercentage = [];
  private checkCounter = [];
  public showPolicy = false;
  private hidePolicyTimer: any;
  private destroyRef = inject(DestroyRef);

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loginService: LoginService,
    private _utils: UtilsService,
    private cookieService: CookieService,
    private sessionStorage: SessionStorageService,
    @Inject(DOCUMENT) private document: any,
    private _domainDataService: DomainDataService
  ) {
    this._changePwdSuccessSubscriber = this._changePwdSuccessSubscriber.bind(this);
    this._changePwdErrorSubscriber = this._changePwdErrorSubscriber.bind(this);
    this._setPwdPolicy = this._setPwdPolicy.bind(this);
  }

  public ngOnInit() {
    this.domainName = btoa(window.location.host);
    this._domainDataService.setDomain(this.domainName);
    this._loadPasswordPolicy();
  }

  public onSubmit() {
    this.isSubmitted = true;
    if (this.formGroup.invalid) {
      return;
    }

    this.changePwdForm.encCurrentPassword = encrypt(this.changePwdForm.currentPassword);
    this.changePwdForm.encNewPassword = encrypt(this.changePwdForm.newPassword);
    this.changePwdForm.encConfirmPassword = encrypt(this.changePwdForm.confirmPassword);

    this._loginService.changePassword(this.changePwdForm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this._changePwdSuccessSubscriber, this._changePwdErrorSubscriber);
  }

  public get getPassedChecksPercentage(): string {
    return this.checkListPercentage[this.currentCheckListCount - 1] || '0%';
  }

  public onDataChange(event: any): void {
    this.errorMessage = '';
    clearTimeout(this.hidePolicyTimer);
    this.showPolicy = true;

    this.checkCounter = [];
    if (!this.newPasswordCtrl?.hasError('required') && !this.newPasswordCtrl?.hasError('minlength') && this.newPasswordCtrl?.dirty) {
      this.checkCounter.push(true);
    }

    if (!this.newPasswordCtrl?.hasError('required') && !this.newPasswordCtrl?.hasError('maxlength') && this.newPasswordCtrl?.dirty) {
      this.checkCounter.push(true);
    }

    if (this.passwordPolicy.complexity || this.passwordPolicy.allowedSpecialChar) {
      if (!this.newPasswordCtrl?.hasError('required') && !this.newPasswordCtrl?.hasError('pattern') && this.newPasswordCtrl?.dirty) {
        this.checkCounter.push(true);
      }
    }

    this.currentCheckListCount = this.checkCounter.length;

    if (this.newPasswordCtrl && this.newPasswordCtrl.valid) {
      this.hidePolicyTimer = setTimeout(() => {
        this.showPolicy = false;
      }, 2000);
    }
  }

  public onPasswordFocus(): void {
    clearTimeout(this.hidePolicyTimer);
    this.showPolicy = true;
  }

  public onPasswordBlur(): void {
    clearTimeout(this.hidePolicyTimer);
    this.showPolicy = false;
  }

  private fillCheckList(): void {
    this.checkListCount = Object.keys(this.passwordPolicyNote || {}).length < 3 ? Object.keys(this.passwordPolicyNote || {}).length : 3;
    const initialValue = 100 / this.checkListCount;
    this.checkListPercentage = Array.from(new Array(this.checkListCount)).map((e, i) => {
      const percentage = initialValue * (i + 1);
      return percentage + '%';
    });
  }

  private _loadPasswordPolicy() {
    this._loginService.getPasswordPolicy(this.domainName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this._setPwdPolicy);
  }

  private _setPwdPolicy({passwordPolicy, passwordPolicyNotes}: PasswordPolicyResponse) {
    this.isLoading = false;
    if (passwordPolicy.complexity) {
      this.passwordPolicy.pwdRegex = this._getComplextPwdRegex(passwordPolicy);
    }
    if (!passwordPolicy.complexity && passwordPolicy.allowedSpecialChar) {
      this.passwordPolicy.pwdRegex = this._getSpecialCharPwdRegex(passwordPolicy);
    }
    this.passwordPolicy = {...passwordPolicy, ...this.passwordPolicy};
    this.passwordPolicyNote = this._preparePolicyNote(passwordPolicyNotes, passwordPolicy);
    this.fillCheckList();
  }

  private _getComplextPwdRegex({allowedSpecialChar}: PasswordPolicy): RegExp {
    const symbols = `[${allowedSpecialChar.replace(/,/g, '|')}]`;
    if (allowedSpecialChar) {
      return new RegExp('(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])' + '(?=.*' + symbols + ')');
    }
    return new RegExp('(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])');
  }

  private _getSpecialCharPwdRegex({allowedSpecialChar}: PasswordPolicy): RegExp {
    const symbols = `[${allowedSpecialChar.replace(',', '')}]`;
    return new RegExp(symbols);

  }

  private _preparePolicyNote(policyNotes: PasswordPolicyNote[], policyParams: PasswordPolicy) {
    return policyNotes.reduce((model, note) => {
      const replacementObj = Object.keys(policyParams).find((param) => param === note.optKey);
      const description = this._utils.parseTemplateString(note.description, policyParams, '[', ']');
      if (policyParams[replacementObj]) {
        model[replacementObj] = description;
      }
      if (replacementObj === 'complexity' && policyParams.complexity) {
        model['complexity'] = this._getPwdPolicyDescription(description, policyParams);
      }
      return {...model};
    }, <PasswordPolicyNote>{});
  }

  private _getPwdPolicyDescription(policyNoteDescription: string, policyParams: PasswordPolicy) {
    if (policyParams.allowedSpecialChar) {
      return policyNoteDescription + ' ' + policyParams.allowedSpecialChar;
    }
    return OAuthConstants.PASSWORD_POLICY_COMPLEXITY_NOTE;
  }

  private _changePwdSuccessSubscriber(res) {
    const {redirectUrl} = res;
    const domain = window.location.host;
    const context = {domain: domain, targetUrl: redirectUrl};
    const url = this._utils.parseTemplateString(environment.redirectCallbacktemplate, context);
    this.cookieService.deleteAll('/', domain);
    this.sessionStorage.remove('access_token');
    window.location.href = url;
  }

  private _changePwdErrorSubscriber(err) {
    if (!err) {
      return;
    }
    const {error} = err;
    this.errorMessage = error.message;
  }
}
